import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { getAllRedzones, uploadRedzones } from "services";

import { addShapeProcessor, checkValidShapefileSize, checkValidShapefileType, handleFileUpload, processGeometries, useCancelAsyncOnUnmount } from "utils";

import { AdminHead, AdminSection, AdminTable, Button, Card, Col, MainPage, MapInteractive, Row, Typography, Upload, Message as message } from "components";

export const ShapefileUpload = () => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [redzones, setRedzones] = useState([]);
  const [redzoneYear, setRedzoneYear] = useState();

  const signal = useCancelAsyncOnUnmount()();

  const loadRedzones = useCallback(() => {
    getAllRedzones({
      signal,
    }).then((res) => {
      setRedzones(res.redzones);
      setRedzoneYear(res.year);
      setLoading(false);
    });
  }, [signal]);

  const onError = (error) => {
    message({
      content: `File processing failed with the error ${error}.`,
      icon: "gremlin",
    });
  };

  const processShapes = async (data) => {
    const processedGeometries = await processGeometries(data, onError);
    uploadRedzones({
      signal,
      redzones: processedGeometries,
    })
      .then(() => loadRedzones())
      .finally(() => setUploading(false));
  };

  const processShapeFile = (file) => {
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();

    reader.onabort = () =>
      message({
        content: "File reading was aborted unexpectedly. Try again.",
        icon: "gremlin",
      });

    reader.onerror = () =>
      message({
        content: "File reading was failed unexpectedly. Try again.",
        icon: "gremlin",
      });

    reader.onloadend = async () => {
      const binaryStr = reader.result;

      if (fileType === "zip") {
        processShapes(binaryStr);
      } else {
        message({
          content: "You must upload a valid zip file with shp, prj and dbf files included.",
          icon: "gremlin",
        });
      }
    };
    if (fileType === "zip") {
      reader.readAsArrayBuffer(file);
    } else {
      message({
        content: "You must upload a valid zip file with shp, prj and dbf files included.",
        icon: "gremlin",
      });
    }
  };

  const handleUpload = (file) => {
    setUploading(true);

    handleFileUpload({
      file: file,
      location: "shapefiles",
    }).then(() => {
      processShapeFile(file);
      message({
        content: "Your file was accepted by the gremlins.",
        icon: "gremlin",
      });
    });
  };

  const fileUpload = async (file) => {
    const isValidType = await checkValidShapefileType(file);
    const isValidSize = await checkValidShapefileSize(file);

    if (!isValidType) {
      message({
        content: "Gremlins only eat zip files.",
        icon: "gremlin",
      });
    } else if (!isValidSize) {
      message({
        content: "Your file was too large for the gremlins.",
        icon: "gremlin",
      });
    } else {
      handleUpload(file);
    }
  };

  useEffect(() => {
    addShapeProcessor();
    loadRedzones();
  }, [loadRedzones]);

  const currentYearRedzonesUploaded = redzoneYear === moment().year();

  const getTableData = () => {
    const states = [...new Set(redzones.map(({ state }) => state))];

    return states.map((state) => ({
      state,
      numberOfZones: redzones.filter((redzone) => redzone.state === state).length,
      accurate: redzones.find((redzone) => redzone.state === state).accurate,
    }));
  };

  return (
    <MainPage loading={loading} pageName="admin">
      <AdminHead
        headline="Red zones shapefile upload"
        description="Upload red zones shapefiles here"
        extra={
          <Upload name="file" showUploadList={false} action="" beforeUpload={(v) => fileUpload(v)} className="auto-height redzone-upload">
            <Button disabled={currentYearRedzonesUploaded} type="primary" loading={uploading}>
              Upload zip
            </Button>
          </Upload>
        }
      />
      {!loading && (
        <div className="content">
          {redzones.length ? (
            <>
              <Row>
                <Col>
                  <Typography variant="paragraph-m">Redzones from {redzoneYear} have been uploaded and are currently active.</Typography>
                  {!currentYearRedzonesUploaded ? (
                    <>
                      <Typography variant="paragraph-m">You can upload a new redzone shapefile for {moment().year()} using the upload button above.</Typography>
                      <b>Please note: this can only be updated once a year.</b>
                    </>
                  ) : null}
                </Col>
              </Row>
              <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]}>
                <Col>
                  <Card className="map-card">
                    <MapInteractive
                      defaultZoom={7}
                      geometries={redzones.map((geometry) => ({
                        geometry: geometry.geometry,
                        type: "shapeFile",
                      }))}
                      location={{
                        lat: 52.5224832,
                        lng: 13.3906564,
                      }}
                      scaleControl={true}
                      zoomControl={true}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          ) : null}

          <AdminSection headline="Uploaded red zone shape files" description="Federal states missing from the table have 0 red zones" />

          <Row>
            <Col>
              {!redzones.length ? <Typography variant="paragraph-m">No redzones have been added</Typography> : <AdminTable dataSource={getTableData()} />}
            </Col>
          </Row>
        </div>
      )}
    </MainPage>
  );
};
